import { useCallback, useMemo, useEffect } from 'react'
import { useDataValue, useDataChange, useDataSubmit } from 'Simple/Data.js'

import debounce from 'lodash/debounce.js'

export function useOnMoveBoard(props) {
  let boardOrderPreferences = useDataValue({
    context: 'boards',
    path: 'board_order_preferences',
    viewPath: props.viewPath,
  })

  let submit = useDataSubmit({
    context: 'boards',
    viewPath: props.viewPath,
  })

  let debouncedSave = useMemo(
    () =>
      debounce(async newOrder => {
        await submit({ type: 'move_board', board_order: newOrder })
      }, 250),
    [submit]
  )

  useEffect(() => {
    return () => {
      debouncedSave.cancel()
    }
  }, [debouncedSave])

  return useCallback(
    function onMove(
      board_id,
      from_list,
      to_list,
      toIndex,
      sourceBoards = [],
      destBoards = []
    ) {
      // Get or initialize custom orders for both lists
      let currentFromList =
        boardOrderPreferences?.[from_list] || sourceBoards.map(b => b.id)
      let currentToList =
        boardOrderPreferences?.[to_list] || destBoards.map(b => b.id)

      // Remove from source list
      let updatedFromList = currentFromList.filter(id => id !== board_id)

      // Insert into destination list at specific index
      let updatedToList = [...currentToList]
      updatedToList.splice(toIndex, 0, board_id)

      let newBoardOrder = {
        ...boardOrderPreferences,
        [from_list]: updatedFromList,
        [to_list]: updatedToList,
      }

      // Persist to backend
      debouncedSave(newBoardOrder)
    },
    [boardOrderPreferences, debouncedSave]
  )
}

export function useOnReorderBoard(props) {
  let boards = useDataValue({
    context: 'boards',
    path: 'boards',
    viewPath: props.viewPath,
  })

  let changeBoards = useDataChange({
    context: 'boards',
    path: 'boards',
    viewPath: props.viewPath,
  })

  let list_type = useDataValue({
    context: 'task_boards_list',
    path: 'type',
    viewPath: props.viewPath,
  })

  let boardOrderPreferences = useDataValue({
    context: 'boards',
    path: 'board_order_preferences',
    viewPath: props.viewPath,
  })

  let submit = useDataSubmit({
    context: 'boards',
    viewPath: props.viewPath,
  })

  let debouncedSave = useMemo(
    () =>
      debounce(async newOrder => {
        await submit({ type: 'move_board', board_order: newOrder })
      }, 250),
    [submit]
  )

  useEffect(() => {
    return () => {
      debouncedSave.cancel()
    }
  }, [debouncedSave])

  return useCallback(
    function onReorder(fromIndex, toIndex) {
      if (!boards || fromIndex === toIndex) return

      let reordered = [...boards]
      let [movedItem] = reordered.splice(fromIndex, 1)
      reordered.splice(toIndex, 0, movedItem)
      changeBoards(reordered)

      debouncedSave({
        ...boardOrderPreferences,
        [list_type]: reordered.map(b => b.id),
      })
    },
    [boards, list_type, boardOrderPreferences, changeBoards, debouncedSave]
  )
}
